import React, { useState, useEffect } from 'react'
import { Users, Building, Settings, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react'
import { adminApi } from '../services/api'
import { format } from 'date-fns'

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalUsers: 0,
    totalFeatures: 0,
    activeTenants: 0,
    recentTenants: [],
    recentActivity: [],
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await adminApi.getStats()
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
      setError('Failed to load dashboard stats')
    } finally {
      setIsLoading(false)
    }
  }

  const statCards = [
    {
      title: 'Toplam Firma',
      value: stats.totalTenants,
      icon: Building,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-100'
    },
    {
      title: 'Toplam Kullanıcı',
      value: stats.totalUsers,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-100'
    },
    {
      title: 'Aktif Modüller',
      value: stats.totalFeatures,
      icon: Settings,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-100'
    },
    {
      title: 'Aktif Firmalar',
      value: stats.activeTenants,
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-100'
    },
  ]

  if (isLoading) {
    return (
      <div className="page-container animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-96 bg-gray-200 rounded-xl"></div>
          <div className="h-96 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-red-50 p-6 rounded-full mb-4">
          <AlertCircle className="h-10 w-10 text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Veriler Yüklenemedi</h3>
        <p className="text-gray-500 mb-6">{error}</p>
        <button onClick={fetchStats} className="btn btn-primary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Tekrar Dene
        </button>
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Genel Bakış</h1>
          <p className="page-subtitle">StoklyApp V3 sistem durumu ve istatistikleri</p>
        </div>
        <button onClick={fetchStats} className="btn btn-secondary btn-sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card) => (
          <div key={card.title} className={`glass-card p-6 border-l-4 ${card.borderColor}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-500 mb-1">{card.title}</p>
                <p className="text-3xl font-bold text-secondary-900">{card.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${card.bgColor}`}>
                <card.icon className={`h-6 w-6 ${card.color}`} />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-secondary-400">
              <TrendingUp className="w-3 h-3 mr-1 text-green-500" />
              <span className="text-green-600 font-medium mr-1">+12%</span>
              <span>geçen aya göre</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tenants */}
        <div className="lg:col-span-2 glass-card flex flex-col">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-secondary-900">Son Kayıt Olan Firmalar</h3>
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">Tümünü Gör</button>
          </div>

          <div className="flex-1 overflow-auto">
            {stats.recentTenants && stats.recentTenants.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {stats.recentTenants.map((tenant) => (
                  <div key={tenant.id} className="flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 font-bold text-sm">
                        {tenant.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900">{tenant.name}</p>
                        <p className="text-sm text-secondary-500">{tenant.subdomain}.stoklyapp.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-secondary-400">Kayıt Tarihi</p>
                        <p className="text-sm text-secondary-600">
                          {format(new Date(tenant.createdAt), 'dd MMM yyyy')}
                        </p>
                      </div>
                      <span className={`badge ${tenant.status === 'active' ? 'badge-success' :
                          tenant.status === 'trial' ? 'badge-info' : 'badge-warning'
                        }`}>
                        {tenant.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Users className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-secondary-500">Henüz kayıtlı firma yok</p>
              </div>
            )}
          </div>
        </div>

        {/* System Status */}
        <div className="glass-card">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-secondary-900">Sistem Durumu</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-sm font-medium text-secondary-700">API Sunucusu</span>
              </div>
              <span className="badge badge-success">Online</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-sm font-medium text-secondary-700">Veritabanı</span>
              </div>
              <span className="badge badge-success">Bağlı</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="text-sm font-medium text-secondary-700">Depolama</span>
              </div>
              <span className="text-sm text-secondary-500">45% Dolu</span>
            </div>

            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{ width: '45%' }}></div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary-500">Son Yedekleme</span>
                <span className="font-medium text-secondary-900">
                  {format(new Date(), 'dd MMM HH:mm')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard 